import { useEffect } from 'react';
import { NegotiationKeys } from '@enums/react-query-keys';
import logger from '@helpers/utils/logger/client';
import { BuyingOffersResponse } from '@interfaces/models/negotiation/domain/BuyingOffer';
import { UserTransactionRole } from '@interfaces/models/userTransactionRole';
import { NegotiationsService } from '@services/negotiations-service';
import useUser from '@hooks/user/use-user';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  DEFAULT_BUYING_OFFERS_PER_PAGE,
  DEFAULT_LISTINGS_PRODUCTS_PER_PAGE,
  DEFAULT_SELLING_OFFERS_NEGOTIATIONS_PER_PAGE,
  DEFAULT_SELLING_OFFERS_PRODUCTS_PER_PAGE,
} from '@constants/negotiation';
import {
  SellingOffersProduct,
  SellingOffersProductListResponse,
} from '@interfaces/models/negotiation/domain/SellingOffersProduct';
import { useBuyingOffers } from '@context/buying-offers.context';
import { useSellingOffers } from '@context/selling-offers.context';
import { useListItemSelectContext } from '@context/negotiation-listings-split-view.context';
import { useListings } from '@context/listings.context';
import { ListingsProducts } from '@interfaces/models/negotiation/domain/ListingsProducts';
import { ListingsItemToShip } from '@interfaces/models/negotiation/domain/ListingsItemsToShip';
import { SellingOffersNegotiationListResponse } from '@interfaces/models/negotiation/domain/SellingOffersNegotiation';

interface PaginationState {
  currentPage: number;
  totalPages: number;
  totalCount?: number;
}

// Mapping layer for the BuyingOffersResponse
const mapToPaginationState = (count: number, limit: number, offset: number): PaginationState => {
  return {
    currentPage: offset / limit + 1,
    totalPages: Math.ceil(count / limit),
    totalCount: count,
  };
};

export const useGetListBuyingOfferNegotiations = (limit = DEFAULT_BUYING_OFFERS_PER_PAGE, offset = 0) => {
  const { user } = useUser();
  const { setBuyingOffersList, setBuyingOffersPaginationMeta } = useBuyingOffers();

  const queryFn = async (): Promise<BuyingOffersResponse> => {
    const response = await NegotiationsService.listBuyingOffersNegotiations(limit, offset);
    return response;
  };

  return useQuery({
    queryKey: [NegotiationKeys.NEGOTIATION_BUYING_OFFERS, limit, offset, user?.id],
    queryFn,
    keepPreviousData: true,
    onSuccess: (res) => {
      setBuyingOffersList(res.data);
      setBuyingOffersPaginationMeta(mapToPaginationState(res.meta.count, res.meta.limit, res.meta.offset));
    },
    onError: (error: AxiosError) => {
      logger.error(error, 'Error occurred while fetching the list of buying offer negotiations');
    },
  });
};

export const useGetSellingOfferProductById = (
  productId: number | null,
  role: UserTransactionRole = 'seller',
  limit = DEFAULT_SELLING_OFFERS_PRODUCTS_PER_PAGE,
  offset = 0,
) => {
  const { setSelectedListItemProduct, setHasNegotiationColumnError } = useListItemSelectContext();
  const { user } = useUser();

  const queryFn = async (): Promise<SellingOffersProduct> => {
    if (!productId) {
      return null;
    }
    const response = await NegotiationsService.getSellingOffersProductById(role, limit, offset, productId);
    return response;
  };

  const query = useQuery<SellingOffersProduct>({
    enabled: !!productId,
    queryKey: [NegotiationKeys.NEGOTIATION_SELLING_OFFERS_PRODUCT, limit, offset, user?.id, productId],
    queryFn,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      // Do not retry if error
      if (error instanceof AxiosError) {
        return false;
      }
      return failureCount < 3;
    },
    onError: (error: AxiosError) => {
      // Set error if product fetch from URL returns any error
      logger.error(error, `Error occured while fetching a single product with ID: ${productId}`);
      setHasNegotiationColumnError(true);
    },
  });

  useEffect(() => {
    if (query.data) {
      setSelectedListItemProduct(query.data);
      setHasNegotiationColumnError(false);
    }
  }, [query.data, setHasNegotiationColumnError, setSelectedListItemProduct]);

  return query;
};

export const useGetListSellingOfferProducts = (
  role: UserTransactionRole = 'seller',
  limit = DEFAULT_SELLING_OFFERS_PRODUCTS_PER_PAGE,
  offset = 0,
) => {
  const { user } = useUser();
  const { setSellingOffersProductsList, setSellingOffersProductsPaginationMeta } = useSellingOffers();

  const queryFn = async (): Promise<SellingOffersProductListResponse> => {
    const response = await NegotiationsService.listSellingOffersProducts(role, limit, offset);
    return response;
  };

  return useQuery<SellingOffersProductListResponse>({
    queryKey: [NegotiationKeys.NEGOTIATION_SELLING_OFFERS_PRODUCTS, limit, offset, user?.id],
    queryFn,
    keepPreviousData: true,
    onSuccess: (res: SellingOffersProductListResponse) => {
      setSellingOffersProductsList(res.items);
      setSellingOffersProductsPaginationMeta(
        mapToPaginationState(res.pagination.totalCount, res.pagination.limit, res.pagination.offset),
      );
    },
    onError: (error: AxiosError) => {
      logger.error(error, 'Error occurred while fetching the list of selling offer products');
    },
  });
};

export const useGetListSellingOfferNegotiations = (
  id: number,
  role: UserTransactionRole = 'seller',
  limit = DEFAULT_SELLING_OFFERS_NEGOTIATIONS_PER_PAGE,
  offset = 0,
) => {
  const { user } = useUser();
  const { setSellingOffersNegotiationsList, setSellingOffersNegotiationsPaginationMeta } = useSellingOffers();
  const { setHasNegotiationColumnError } = useListItemSelectContext();

  const queryFn = async (): Promise<SellingOffersNegotiationListResponse> => {
    const response = await NegotiationsService.listSellingOffersNegotiations(id, role, limit, offset);
    return response;
  };

  return useQuery({
    enabled: !!id,
    queryKey: [NegotiationKeys.NEGOTIATION_SELLING_OFFERS_NEGOTIATIONS, id, limit, offset, user?.id],
    queryFn,
    keepPreviousData: true,
    onSuccess: (res) => {
      if (res?.data?.length > 0) {
        setSellingOffersNegotiationsList(res.data);
        setSellingOffersNegotiationsPaginationMeta(
          mapToPaginationState(res.meta.count, res.meta.limit, res.meta.offset),
        );
        setHasNegotiationColumnError(false);
      } else {
        // Set error if negotiations list returns empty
        setHasNegotiationColumnError(true);
      }
    },
    onError: (error: AxiosError) => {
      logger.error(error, 'Error occurred while fetching the list of selling offer negotiations');
    },
  });
};

export const useGetListListingsProducts = (sortBy, limit = DEFAULT_LISTINGS_PRODUCTS_PER_PAGE, offset = 0) => {
  const { user } = useUser();
  const { setListingsProductsList, setListingsProductsPaginationMeta } = useListings();

  const queryFn = async (): Promise<ListingsProducts> => {
    const response = await NegotiationsService.listListingsProducts(limit, offset, sortBy);
    return response;
  };

  return useQuery({
    queryKey: [NegotiationKeys.NEGOTIATION_LISTINGS_PRODUCTS, limit, offset, user?.id, sortBy],
    queryFn,
    keepPreviousData: true,
    onSuccess: (res) => {
      setListingsProductsList(res.data);
      setListingsProductsPaginationMeta(mapToPaginationState(res.meta.count, res.meta.limit, res.meta.offset));
    },
    onError: (error: AxiosError) => {
      logger.error(error, 'Error occurred while fetching the list of listings products');
    },
  });
};

export const useGetProductsForOfferSettings = (dateEnable: boolean) => {
  return useQuery({
    queryKey: [NegotiationKeys.NEGOTIATION_OFFERSETTINGS_PRODUCTS, dateEnable],
    queryFn: () => NegotiationsService.offerSettingsProducts(),
    enabled: dateEnable,
    onError: (error: AxiosError) => {
      logger.error(error, 'Error occurred while fetching the list of offerSettings products');
    },
  });
};

export const useGetListListingsItemsToShip = (limit = DEFAULT_LISTINGS_PRODUCTS_PER_PAGE, offset = 0) => {
  const { user } = useUser();
  const { setListingsItemsToShipList } = useListings();

  const queryFn = async (): Promise<ListingsItemToShip[]> => {
    const response = await NegotiationsService.listListingsItemsToShip(limit, offset);
    return response;
  };

  return useQuery({
    queryKey: [NegotiationKeys.NEGOTIATION_LISTINGS_ITEMS_TO_SHIP, limit, offset, user?.id],
    queryFn,
    keepPreviousData: true,
    onSuccess: (res) => {
      setListingsItemsToShipList(res);
    },
    onError: (error: AxiosError) => {
      logger.error(error, 'Error occurred while fetching the list of listings items to ship');
    },
  });
};
